import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AiFillEye, AiFillEyeInvisible, AiOutlineDelete } from "react-icons/ai";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import useTeamMembers from "containers/team-management/useHooks";
import { teamMemberDTO } from "utils/helpers/models/team-management/team-members.dto";
import { TEAM_MEMBER_ROLES } from "utils/helpers/enums/roles.enum";
import useUtils from "hooks/useUtils";
import editProfileUSer from "assets/images/common/others/editProfileUSer.png";
import { useSelector } from "react-redux";
import { EditProfile } from "assets/images/common/svgs";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { warningToaster } from "utils/helpers/common/alert-service";
import { LANGUAGES } from "utils/helpers/enums/shared.enum";

const EditTeamMember: React.FC = () => {
  const {
    register,
    handleSubmit,
    getValues,
    clearErrors,
    setValue,
    reset,
    formState: { errors },
  } = useForm<teamMemberDTO>();
  const [formData, setFormData] = useState<teamMemberDTO>(new teamMemberDTO());
  const [profilePicPreview, setProfilePicPreview] = useState<string>("");
  const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [data, setData] = useState<teamMemberDTO | null>(null);
  const navigate = useNavigate();
  const { getQueryParams } = useUtils();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const params = getQueryParams();
  const { getTeamMemberByID, editTeamMember, uploadFile, getRolesInTeam } = useTeamMembers();

  const [roles, setRoles] = useState<any[]>([]);

  useEffect(() => {
    getRolesInTeam(setRoles, { page: 0, size: 100 });
  }, []);


  useEffect(() => {
    if (params?.id) {
      getTeamMemberByID(params.id, (fetchedData: teamMemberDTO) => {
        setData(fetchedData);
        reset({
          ...fetchedData,
        });

        if (fetchedData.profilePic && fetchedData.profilePic.name) {
          setProfilePicPreview(
            concatPathWithBackendUrl(fetchedData.profilePic.name)
          );
        } else {
          setProfilePicPreview("");
        }
      });
    }
  }, [params?.id, reset]);

  // Set roleId when both data and roles are available
  useEffect(() => {
    if (data && roles.length > 0) {
      let matchedRoleId: number | undefined = data.roleId;
      
      // Find roleId by matching currentRole or defaultRole with roles list
      if (data.currentRole || data.defaultRole) {
        const roleToMatch = data.currentRole || data.defaultRole;
        const matchedRole = roles.find(
          (role) => role.name?.toUpperCase() === roleToMatch?.toUpperCase()
        );
        if (matchedRole) {
          matchedRoleId = matchedRole.id;
        }
      }

      if (matchedRoleId) {
        setValue("roleId", matchedRoleId, { shouldValidate: true });
      } else if (data.roleId) {
        setValue("roleId", data.roleId, { shouldValidate: true });
      }
    }
  }, [data, roles, setValue]);

  const uploadProfilePic = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setProfilePicFile(file);
      setProfilePicPreview(URL.createObjectURL(file));
      clearErrors("profilePicUrl");
    }
  };

  const handleProfilePicDelete = () => {
    setProfilePicFile(null);
    setProfilePicPreview("");
    setValue("profilePicUrl", "");
  };

  const onSubmit = async (formData: teamMemberDTO) => {
    let profilePicId = formData.profilePicId;
    if (!profilePicFile && !profilePicPreview) {
      warningToaster("Please upload your profile picture");
      return;
    }

    if (profilePicFile) {
      const form_data = new FormData();
      form_data.append("file", profilePicFile);
      const imageData: any = await uploadFile(form_data);
      profilePicId = imageData?.id;
    }

    const roleObj = roles.find((r) => r.id === Number(formData.roleId));

    if (!roleObj) {
      warningToaster("Selected role not found");
      return;
    }

    const roleName = roleObj.name;

    const payload: any = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      gender: formData.gender,
      dob: formData.dob,
      language: formData.language,
      state: formData.state,
      city: formData.city,
      zipCode: formData.zipCode,
      role: roleName,
      roleId: roleObj.id,
      defaultRole: roleName,
      profilePicId: profilePicId,
    };

    const dataAny = data as any;
    if (dataAny?.active !== undefined) {
      payload.active = dataAny.active;
    }
    if (dataAny?.phone) {
      payload.phone = dataAny.phone;
    }

    await editTeamMember(params?.id, payload);
  };


  const goToBack = () => {
    navigate(-1);
  };

  return (
    <div className="">
      <h1 className="text-base text-primaryText font-bold mb-6 flex items-center gap-3">
        <span
          onClick={goToBack}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Edit Team Member
      </h1>

      <div className="w-full bg-white p-4 rounded-[8px]">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-3 mt-3"
        >
          <div>
            <div className="flex flex-col items-center w-[270px]">
              <div className="relative">
                <img
                  src={profilePicPreview || editProfileUSer}
                  alt="Profile"
                  className="w-36 h-36 rounded-full object-cover"
                />
                {profilePicPreview ? (
                  <button
                    type="button"
                    onClick={handleProfilePicDelete}
                    className="absolute bottom-2 right-0 bg-red-500 text-white p-2 rounded-full cursor-pointer"
                  >
                    <AiOutlineDelete />
                  </button>
                ) : (
                  <label
                    htmlFor="profilePicUrl"
                    className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                  >
                    <EditProfile />
                  </label>
                )}
                <input
                  id="profilePicUrl"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  {...register("profilePicUrl")}
                  onChange={uploadProfilePic}
                />
              </div>
              {errors.profilePicUrl && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.profilePicUrl.message}
                </p>
              )}
              <div className="flex flex-col gap-2 items-center mt-3">
                <p className="text-base text-label px-5 font-semibold">
                  Profile Picture
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {/* First Name */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">First Name</label>
              <input
                type="text"
                placeholder="Enter First Name"
                {...register("firstName", {
                  required: "First Name is required",
                })}
                className="custom-input"
              />
              {errors.firstName && (
                <span className="text-red-500 text-sm">
                  {errors.firstName.message}
                </span>
              )}
            </div>

            {/* Last Name */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">Last Name</label>
              <input
                type="text"
                placeholder="Enter Last Name"
                {...register("lastName", { required: "Last Name is required" })}
                className="custom-input"
              />
              {errors.lastName && (
                <span className="text-red-500 text-sm">
                  {errors.lastName.message}
                </span>
              )}
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">Email</label>
              <input
                type="email"
                placeholder="Enter Email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Invalid email address",
                  },
                })}
                className="custom-input"
              />
              {errors.email && (
                <span className="text-red-500 text-sm">
                  {errors.email.message}
                </span>
              )}
            </div>

            {/* Gender */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">Gender</label>
              <select
                {...register("gender", { required: "Gender is required" })}
                className="custom-input"
              >
                <option value="">Select Gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
              
              </select>
              {errors.gender && (
                <span className="text-red-500 text-sm">
                  {errors.gender.message}
                </span>
              )}
            </div>

            {/* Date of Birth */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">Date of Birth</label>
              <input
                type="date"
                {...register("dob", { required: "Date of Birth is required" })}
                className="custom-input"
              />
              {errors.dob && (
                <span className="text-red-500 text-sm">
                  {errors.dob.message}
                </span>
              )}
            </div>

            {/* Language */}
            <div className="flex flex-col gap-2">
              <label className="custom-label">Language</label>
             
                 <select
                className="custom-input"
                {...register("language", {
                  required: "Language is required",
                })}

              >
                <option value="">Select Language</option>
                {Object.values(LANGUAGES).map((lang, index) => {
                  return <option key={index}>{lang}</option>;
                })}
              </select>
              {errors.language && (
                <span className="text-red-500 text-sm">
                  {errors.language.message}
                </span>
              )}
            </div>

            {/* State */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">State</label>
              <input
                type="text"
                placeholder="Enter State"
                {...register("state", { required: "State is required" })}
                className="custom-input"
              />
              {errors.state && (
                <span className="text-red-500 text-sm">
                  {errors.state.message}
                </span>
              )}
            </div>

            {/* City */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">City</label>
              <input
                type="text"
                placeholder="Enter City"
                {...register("city", { required: "City is required" })}
                className="custom-input"
              />
              {errors.city && (
                <span className="text-red-500 text-sm">
                  {errors.city.message}
                </span>
              )}
            </div>

            {/* Zip Code */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">Zip Code</label>
              <input
                type="text"
                placeholder="Enter Zip Code"
                {...register("zipCode", { required: "Zip Code is required" })}
                className="custom-input"
              />
              {errors.zipCode && (
                <span className="text-red-500 text-sm">
                  {errors.zipCode.message}
                </span>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1">
              <label className="custom-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Password"
                  {...register("password", {
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                  })}
                  className="custom-input"
                />
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <AiFillEyeInvisible size={20} />
                  ) : (
                    <AiFillEye size={20} />
                  )}
                </div>
              </div>
              {errors.password && (
                <span className="text-red-500 text-sm">
                  {errors.password.message}
                </span>
              )}
            </div>

            {/* Status */}
            {/* <div className="flex flex-col gap-2">
              <label className="text-labelText">Status</label>
              <select
                {...register("status", { required: "Status is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none bg-white"
              >
                <option value="">Select Status</option>
                {Object.values(USER_STATUSES).map((type, index) => (
                  <option value={type} key={index} className="capitalize">
                    {type.toUpperCase()}
                  </option>
                ))}
              </select>
              {errors.status && (
                <span className="text-red-500 text-sm">
                  {errors.status.message}
                </span>
              )}
            </div> */}

            <div className="flex flex-col gap-1">
              <label className="custom-label">Role</label>
              <select
                {...register("roleId", { required: "Role is required" })}
                className="custom-input"
              >
                <option value="">Select Role</option>
                {roles.map((type) => (
                  <option value={type.id.toString()} key={type.id} className="capitalize">
                    {type.name}
                  </option>
                ))}
              </select>

              {errors.roleId && (
                <span className="text-red-500 text-sm">
                  {errors.roleId.message}
                </span>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-start mt-5">
            <button
              type="submit"
              className="w-[125px] h-[45px] text-sm font-semibold text-white bg-goldenYellowBg rounded-[8px]"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTeamMember;
